import { useAurigaContext } from '@context/AurigaContext';
import { getSharedExcelExportOverride, setSharedExcelExportChoice } from '@utils/sharedExcelExport';
import { useCallback, useState } from 'react';

/** The shared Excel export state for this browser. */
export interface SharedExcelExportState {
  /** Whether Download uses the shared export: this browser's override if set, otherwise the code flag. */
  isOn: boolean;
  /** Whether this browser overrides the code flag (an admin chose a path different from it). */
  isLocal: boolean;
  /**
   * Choose the path for this browser.
   *
   * @param useShared - `true` for the shared export, `false` for the legacy export. Choosing the code
   *   flag's own value clears the override.
   */
  setOn: (useShared: boolean) => void;
}

/**
 * Which Excel export Download uses, combining the code flag `enableSharedExcelExport` (from
 * `AurigaContext`) with this browser's admin override (see `utils/sharedExcelExport.ts`).
 *
 * @returns The current path, whether it is a local override, and a setter for the Admin panel switch.
 */
export const useSharedExcelExport = (): SharedExcelExportState => {
  const { featureFlags } = useAurigaContext();
  const flag = featureFlags.enableSharedExcelExport;
  const [override, setOverride] = useState(getSharedExcelExportOverride);

  const setOn = useCallback((useShared: boolean) => setOverride(setSharedExcelExportChoice(useShared, flag)), [flag]);

  return { isOn: override ?? flag, isLocal: override !== null, setOn };
};
